import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Plus, FileDown, FileSpreadsheet, Upload, Trash2, Pencil, Laptop, History, Save, Check, ChevronsUpDown, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { exportRowsToXlsx } from "../../lib/exportToXlsx";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";

const STATUS_CHOICES = [
  { value: "in_use", label: "In Use", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "available", label: "Available", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "repair", label: "Repair", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "retired", label: "Retired", color: "bg-slate-200 text-slate-700 border-slate-300" },
];
const statusMeta = (s) => STATUS_CHOICES.find((x) => x.value === s) || STATUS_CHOICES[1];

export default function LaptopTracker() {
  const { user } = useAuth();
  const isAdmin = ["partner", "admin"].includes(user?.role);

  if (isAdmin) return <AdminView />;
  return <EmployeeView />;
}

// -------------------- EMPLOYEE VIEW --------------------
function EmployeeView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/laptops/mine"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="laptop-employee-view">
      <div>
        <div className="eyebrow">My Hardware</div>
        <h1 className="font-sans text-3xl flex items-center gap-2"><Laptop className="h-7 w-7 text-slate-700"/>My Laptop</h1>
        <p className="text-sm text-slate-600 mt-1">Update the status and notes of the laptop assigned to you. Changes are logged for the Partner &amp; HR/Admin.</p>
      </div>
      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <Card className="p-8 text-center">
          <div className="text-slate-500">No laptop is currently assigned to you.</div>
          <div className="text-xs text-slate-400 mt-2">If this looks wrong, please contact HR/Admin.</div>
        </Card>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((r) => <EmployeeCard key={r.id} row={r} onSaved={load}/>)}
      </div>
    </div>
  );
}

function EmployeeCard({ row, onSaved }) {
  const [status, setStatus] = useState(row.status);
  const [notes, setNotes] = useState(row.notes || "");
  const save = async () => {
    try {
      await api.patch(`/laptops/mine/${row.id}`, { status, notes });
      toast.success("Update sent. Partner & HR/Admin have been notified via the edit log.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const m = statusMeta(status);
  return (
    <Card className="p-5" data-testid={`emp-laptop-${row.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-xs text-slate-500">{row.asset_tag}</div>
          <div className="font-medium text-slate-900">{row.brand} <span className="text-slate-500">{row.model}</span></div>
          <div className="text-xs text-slate-500 mt-1">Serial: <span className="font-mono">{row.serial_no}</span></div>
        </div>
        <Badge className={`border ${m.color}`}>{m.label}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
        <div><span className="text-slate-500">Assigned since:</span> <span className="font-mono">{row.assign_date || "—"}</span></div>
        <div><span className="text-slate-500">Warranty until:</span> <span className="font-mono">{row.warranty_expiry || "—"}</span></div>
      </div>
      <div className="mt-4 space-y-3">
        <div>
          <label className="text-xs text-slate-500">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={`emp-status-${row.id}`}><SelectValue/></SelectTrigger>
            <SelectContent>{STATUS_CHOICES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Notes</label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Report an issue, request repair, etc." data-testid={`emp-notes-${row.id}`}/>
        </div>
        <Button onClick={save} data-testid={`emp-save-${row.id}`}><Save className="h-4 w-4 mr-1"/>Save Update</Button>
      </div>
    </Card>
  );
}

// -------------------- ADMIN VIEW --------------------
function AdminView() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (q) params.q = q;
    try {
      const { data } = await api.get("/laptops", { params });
      setRows(data);
      const s = await api.get("/laptops/summary");
      setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadEmployees = async () => {
    try { const { data } = await api.get("/users", { params: { status: "active" } }); setEmployees(data); }
    catch { /* silent */ }
  };
  const loadLogs = async () => {
    try { const { data } = await api.get("/laptops/logs", { params: { limit: 500 } }); setLogs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); loadEmployees(); }, [filter]);

  const exportXlsx = () => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    exportRowsToXlsx({
      filename: `laptops_${filter === "all" ? "all" : filter}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Laptops",
      columns: [
        { key: "asset_tag", label: "Asset Tag" },
        { key: "brand", label: "Brand" },
        { key: "model", label: "Model" },
        { key: "serial_no", label: "Serial No." },
        { key: "assigned_name", label: "Assigned To" },
        { key: "assigned_to", label: "Employee Code" },
        { key: "assign_date", label: "Assign Date" },
        { key: "purchase_date", label: "Purchase Date" },
        { key: "purchase_cost", label: "Cost (₹)" },
        { key: "warranty_expiry", label: "Warranty Expiry" },
        { key: "status", label: "Status" },
        { key: "notes", label: "Notes" },
      ],
      rows,
    });
  };
  const downloadTemplate = async () => {
    const { data } = await api.get("/laptops/import/template", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([data]));
    a.download = "laptop_import_template.xlsx"; a.click();
  };
  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/laptops/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} laptops (${data.skipped} skipped)`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };
  const del = async (id) => {
    if (!window.confirm("Delete this laptop record?")) return;
    try { await api.delete(`/laptops/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="laptop-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Office Admin</div>
          <h1 className="font-sans text-3xl flex items-center gap-2"><Laptop className="h-7 w-7 text-slate-700"/>Laptop &amp; Hardware Tracker</h1>
          <p className="text-sm text-slate-600 mt-1">Track laptops assigned to staff · Warranty · Custodian · Status · Full edit log</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} data-testid="lp-template"><FileSpreadsheet className="h-4 w-4 mr-1"/>Template</Button>
          <label className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 rounded cursor-pointer hover:bg-slate-50" data-testid="lp-import">
            <Upload className="h-4 w-4"/>Import
            <input type="file" accept=".xlsx" className="hidden" onChange={uploadFile}/>
          </label>
          <Button variant="outline" onClick={exportXlsx} data-testid="lp-export"><FileDown className="h-4 w-4 mr-1"/>Export</Button>
          <Button onClick={() => setShowNew(true)} data-testid="lp-new"><Plus className="h-4 w-4 mr-1"/>Add Laptop</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <Kpi label="Total Units" value={summary.total}/>
          <Kpi label="In Use" value={summary.by_status.in_use || 0} accent="emerald"/>
          <Kpi label="Available" value={summary.by_status.available || 0} accent="blue"/>
          <Kpi label="Unused (idle)" value={summary.unused || 0} accent="slate"/>
          <Kpi label="Repair" value={summary.by_status.repair || 0} accent="amber"/>
          <Kpi label="Retired" value={summary.by_status.retired || 0}/>
          <Kpi label="Warranty <90d" value={summary.warranty_expiring_90d} accent="rose"/>
        </div>
      )}

      <Tabs defaultValue="register" onValueChange={(v) => v === "logs" && loadLogs()}>
        <TabsList>
          <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs"><History className="h-3 w-3 mr-1"/>Edit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="register" className="mt-4 space-y-3">
          <Card className="p-3">
            <div className="flex gap-2 items-center flex-wrap">
              <Input placeholder="Search tag, brand, model, serial, assignee…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && load()}
                     className="max-w-sm" data-testid="lp-search"/>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40" data-testid="lp-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_CHOICES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={load}>Apply</Button>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left p-3">Tag</th>
                  <th className="text-left p-3">Brand / Model</th>
                  <th className="text-left p-3">Serial No.</th>
                  <th className="text-left p-3">Assigned To</th>
                  <th className="text-left p-3">Emp. Code</th>
                  <th className="text-left p-3">Assign Date</th>
                  <th className="text-left p-3">Purchased</th>
                  <th className="text-right p-3">Cost</th>
                  <th className="text-left p-3">Warranty</th>
                  <th className="text-left p-3">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const m = statusMeta(r.status);
                  const isIdle = !String(r.assigned_to || "").trim();
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lp-row-${r.id}`}>
                      <td className="p-3 font-mono text-xs">{r.asset_tag}</td>
                      <td className="p-3 font-medium text-slate-800">{r.brand} <span className="text-slate-500">{r.model}</span></td>
                      <td className="p-3 font-mono text-xs">{r.serial_no}</td>
                      <td className="p-3 font-medium text-slate-800" data-testid={`lp-assigned-name-${r.id}`}>{isIdle ? <span className="text-slate-400 italic font-normal">Unassigned</span> : (r.assigned_name || r.assigned_to)}</td>
                      <td className="p-3 font-mono text-xs text-slate-600" data-testid={`lp-assigned-code-${r.id}`}>{r.assigned_to || "—"}</td>
                      <td className="p-3 text-slate-600">{r.assign_date || "—"}</td>
                      <td className="p-3 text-slate-600">{r.purchase_date || "—"}</td>
                      <td className="p-3 text-right font-mono">{r.purchase_cost ? `₹${Number(r.purchase_cost).toLocaleString("en-IN")}` : "—"}</td>
                      <td className="p-3 text-slate-600 text-xs">{r.warranty_expiry || "—"}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          <Badge className={`border ${m.color}`}>{m.label}</Badge>
                          {isIdle && <Badge className="border bg-slate-100 text-slate-700 border-slate-300" data-testid={`lp-unused-${r.id}`}>Unused</Badge>}
                        </div>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setEditRow(r)} data-testid={`lp-edit-${r.id}`}><Pencil className="h-3 w-3"/></Button>
                        <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`lp-del-${r.id}`}><Trash2 className="h-3 w-3"/></Button>
                      </td>
                    </tr>
                  );
                })}
                {!rows.length && <tr><td colSpan="11" className="text-center text-slate-400 p-8">No laptops yet — click &quot;Add Laptop&quot; or Import from Excel.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="p-0 overflow-hidden" data-testid="lp-logs">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Who</th>
                  <th className="text-left p-3">Role</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Laptop</th>
                  <th className="text-left p-3">Changed Fields</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs whitespace-nowrap">{(l.at || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="p-3 font-medium text-slate-800">{l.actor_name}</td>
                    <td className="p-3 text-xs uppercase tracking-wider text-slate-500">{l.actor_role}</td>
                    <td className="p-3"><Badge variant="outline">{l.action}</Badge></td>
                    <td className="p-3 font-mono text-xs">{l.laptop_id}</td>
                    <td className="p-3 text-xs">
                      {Object.keys(l.diff || {}).length === 0 ? <span className="text-slate-400">—</span> :
                        Object.entries(l.diff).map(([k, v]) => (
                          <div key={k}><span className="font-mono">{k}:</span> <span className="text-red-600 line-through">{String(v.from ?? "")}</span> → <span className="text-emerald-700">{String(v.to ?? "")}</span></div>
                        ))
                      }
                    </td>
                  </tr>
                ))}
                {!logs.length && <tr><td colSpan="6" className="text-center text-slate-400 p-8">No edit log entries yet. Add or update a laptop to see history here.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {showNew && <LaptopDialog employees={employees} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
      {editRow && <LaptopDialog employees={employees} row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }}/>}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const map = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200",
    blue: "from-blue-50 to-blue-100 border-blue-200",
    amber: "from-amber-50 to-amber-100 border-amber-200",
    rose: "from-rose-50 to-rose-100 border-rose-200",
  };
  const cls = map[accent] || "from-slate-50 to-white border-slate-200";
  return (
    <div className={`bg-gradient-to-br ${cls} border rounded p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold mt-1 text-slate-900">{value}</div>
    </div>
  );
}

function LaptopDialog({ row, employees = [], onClose, onSaved }) {
  const isEdit = !!row;
  const [f, setF] = useState(row || {
    asset_tag: "", brand: "", model: "", serial_no: "",
    purchase_date: "", purchase_cost: 0, warranty_expiry: "",
    assigned_to: "", assign_date: "", status: "available", notes: "",
  });
  const save = async () => {
    if (!f.asset_tag || !f.serial_no) { toast.error("Asset tag & Serial No. are required"); return; }
    try {
      const payload = { ...f, purchase_cost: parseFloat(f.purchase_cost) || 0 };
      if (isEdit) await api.patch(`/laptops/${row.id}`, payload);
      else await api.post("/laptops", payload);
      toast.success(isEdit ? "Laptop updated" : "Laptop added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="lp-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Laptop" : "Add Laptop"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs text-slate-500">Asset Tag *</label><Input value={f.asset_tag} onChange={(e) => setF({ ...f, asset_tag: e.target.value })} data-testid="lp-tag"/></div>
          <div><label className="text-xs text-slate-500">Serial No. *</label><Input value={f.serial_no} onChange={(e) => setF({ ...f, serial_no: e.target.value })} data-testid="lp-serial"/></div>
          <div><label className="text-xs text-slate-500">Brand</label><Input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} placeholder="Dell / HP / Lenovo"/></div>
          <div><label className="text-xs text-slate-500">Model</label><Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="Latitude 7420"/></div>
          <div><label className="text-xs text-slate-500">Purchase Date</label><Input type="date" value={f.purchase_date || ""} onChange={(e) => setF({ ...f, purchase_date: e.target.value })}/></div>
          <div><label className="text-xs text-slate-500">Purchase Cost (₹)</label><Input type="number" value={f.purchase_cost || 0} onChange={(e) => setF({ ...f, purchase_cost: e.target.value })}/></div>
          <div><label className="text-xs text-slate-500">Warranty Expiry</label><Input type="date" value={f.warranty_expiry || ""} onChange={(e) => setF({ ...f, warranty_expiry: e.target.value })}/></div>
          <div><label className="text-xs text-slate-500">Status</label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="lp-status"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUS_CHOICES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-slate-500">Assigned To (Employee)</label>
            <EmployeeCombobox employees={employees} value={f.assigned_to || ""} onChange={(v) => setF({ ...f, assigned_to: v })}/>
          </div>
          <div><label className="text-xs text-slate-500">Assign Date</label><Input type="date" value={f.assign_date || ""} onChange={(e) => setF({ ...f, assign_date: e.target.value })}/></div>
          <div className="col-span-2"><label className="text-xs text-slate-500">Notes</label><Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })}/></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="lp-save">{isEdit ? "Update" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeCombobox({ employees, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = employees.find((e) => e.employee_code === value || e.name === value);
  const displayLabel = selected
    ? `${selected.employee_code || ""} · ${selected.name}`
    : (value || "Select employee…");
  return (
    <div className="flex items-center gap-1 mt-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className="flex-1 flex items-center justify-between rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50"
            data-testid="lp-assignee-combo"
          >
            <span className={selected ? "text-slate-900" : "text-slate-400"}>{displayLabel}</span>
            <ChevronsUpDown className="h-4 w-4 text-slate-400"/>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput placeholder="Search by code or name…" data-testid="lp-assignee-search"/>
            <CommandList>
              <CommandEmpty>No matching employee.</CommandEmpty>
              <CommandGroup heading="Active Employees">
                {employees.map((e) => {
                  const isSel = e.employee_code === value || e.name === value;
                  return (
                    <CommandItem
                      key={e.id}
                      value={`${e.employee_code || ""} ${e.name} ${e.email || ""}`}
                      onSelect={() => { onChange(e.employee_code || e.name); setOpen(false); }}
                      data-testid={`lp-assignee-opt-${e.employee_code || e.id}`}
                    >
                      <Check className={`mr-2 h-4 w-4 ${isSel ? "opacity-100" : "opacity-0"}`}/>
                      <div className="flex flex-col">
                        <span className="font-medium">{e.name}</span>
                        <span className="text-xs text-slate-500 font-mono">{e.employee_code || "—"} · {e.role}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <button type="button" onClick={() => onChange("")}
                className="p-2 text-slate-400 hover:text-red-600" title="Clear"
                data-testid="lp-assignee-clear">
          <X className="h-4 w-4"/>
        </button>
      )}
    </div>
  );
}
